import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';

// Mirrors the backend `_INTERNAL_LINE_RE` in /app/backend/services/rfq_pdf.py.
// Any line containing these patterns is stripped from supplier-facing PDFs.
const INTERNAL_PATTERNS = [
  /supplier\s*:[^|\n]*(cost|markup|margem|margin|sale)\s*:/i,
  /\bcost\s*:\s*[a-z]{0,4}\s*[\d.,]+/i,
  /\bmarkup\b/i,
  /\bmargem\s*:\s*[\d.,]+\s*%?/i,
  /\bmargin\s*:\s*[\d.,]+\s*%?/i,
  /\bsale\s*:\s*[a-z]{0,4}\s*[\d.,]+/i,
  /\+?\s*[\d.]+\s*%\s*markup/i,
];

/** Returns the list of matched pattern labels, or [] if clean. */
export function detectInternalData(text) {
  if (!text || typeof text !== 'string') return [];
  const matches = new Set();
  for (const line of text.split(/\r?\n/)) {
    const t = line.trim();
    if (!t) continue;
    if (/supplier\s*:[^|\n]*(cost|markup|margem|margin|sale)\s*:/i.test(t)) matches.add('Supplier + Cost/Sale');
    if (/\bcost\s*:\s*[a-z]{0,4}\s*[\d.,]+/i.test(t)) matches.add('Cost:');
    if (/\bmarkup\b/i.test(t)) matches.add('markup');
    if (/\b(margem|margin)\s*:\s*[\d.,]+\s*%?/i.test(t)) matches.add('margin/margem');
    if (/\bsale\s*:\s*[a-z]{0,4}\s*[\d.,]+/i.test(t)) matches.add('Sale:');
    if (/\+?\s*[\d.]+\s*%\s*markup/i.test(t)) matches.add('% markup');
  }
  return Array.from(matches);
}

export function hasInternalData(text) {
  if (!text || typeof text !== 'string') return false;
  return INTERNAL_PATTERNS.some((re) => re.test(text));
}

/** Compact amber warning badge rendered inline below a field. */
const InternalDataWarning = ({ text, className = '', testId }) => {
  const matches = useMemo(() => detectInternalData(text), [text]);
  if (matches.length === 0) return null;
  return (
    <div
      className={`mt-1 flex items-start gap-1.5 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800 ${className}`}
      data-testid={testId || 'internal-data-warning'}
      role="status"
    >
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-[1px] text-amber-600" />
      <span>
        <b>Internal data detected</b> — will be automatically stripped from the supplier PDF
        {matches.length > 0 && (
          <span className="ml-1 font-mono text-[10px] opacity-80">({matches.join(', ')})</span>
        )}
      </span>
    </div>
  );
};

export default InternalDataWarning;
